// /config — awake_config editor (v1-C)
// Spec: docs/plans/admin-tooling.md §10
//
// Layout:
//   Left panel: domain list accordion → key list (live value + draft pill)
//   Right panel: edit form (validator-typed input + notes) → diff view → publish
//   Bottom panel: revision history with restore action
//
// Theme: forensic-terminal — mono keys/values, no bg-white / bg-black literals.

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, Field, Input, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StatusPill } from '@/components/admin/status-pill';
import { StepUpModal } from '@/components/admin/step-up-modal';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DomainKeyEntry {
  key: string;
  live_value: string | null;
  has_draft: boolean;
}

interface DomainGroup {
  domain: string;
  keys: DomainKeyEntry[];
}

interface DomainsResponse {
  ok: boolean;
  domains: DomainGroup[];
}

interface DraftInfo {
  config_field: string;
  draft_value: string;
  drafted_by_staff_id: number;
  drafted_at: number;
  notes: string | null;
}

interface KeyDetailResponse {
  ok: boolean;
  key: string;
  live_value: string | null;
  draft: DraftInfo | null;
  editable: boolean;
  validator_kind: string;
}

interface DiffResponse {
  ok: boolean;
  key: string;
  live_value: string | null;
  draft_value: string | null;
  validator_result: { ok: boolean; error?: string } | null;
}

interface RevisionRow {
  revision_id: number;
  config_field: string;
  prior_value: string | null;
  new_value: string;
  published_by_staff_id: number;
  published_at: number;
  notes: string | null;
}

interface RevisionsResponse {
  ok: boolean;
  key: string;
  revisions: RevisionRow[];
}

// ── Inline modal shell ────────────────────────────────────────────────────────

function ModalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85">
      <div className="relative w-full max-w-lg mx-4 rounded-sm border border-border bg-panel-elev p-5 shadow-[0_24px_48px_rgba(0,0,0,0.4)] space-y-4">
        {children}
      </div>
    </div>
  );
}

// ── PublishModal ──────────────────────────────────────────────────────────────

interface PublishModalProps {
  configKey: string;
  onDone: () => void;
  onCancel: () => void;
}

type PublishPhase = 'stepup' | 'submitting' | 'done' | 'error';

function PublishModal({ configKey, onDone, onCancel }: PublishModalProps) {
  const [phase, setPhase] = useState<PublishPhase>('stepup');
  const [errorMsg, setErrorMsg] = useState('');

  function handleVerified(token: string) {
    setPhase('submitting');
    void (async () => {
      const res = await apiFetch<{ ok: boolean; error?: string }>(
        `/admin/config/${encodeURIComponent(configKey)}/publish`,
        { method: 'POST', headers: { 'X-Stepup-Token': token } },
      );
      if (!res.ok) {
        const err = (res.data as Record<string, unknown>)['error'] as string | undefined;
        setErrorMsg(err ?? 'Publish failed. Please try again.');
        setPhase('error');
        return;
      }
      setPhase('done');
      setTimeout(() => { onDone(); }, 600);
    })();
  }

  if (phase === 'stepup') {
    return (
      <StepUpModal
        actionIntent="config-publish"
        targetUserId={null}
        onVerified={handleVerified}
        onCancel={onCancel}
      />
    );
  }

  return (
    <ModalShell>
      <h2 className="text-base font-semibold text-ink-high font-mono">
        Publish <span className="text-primary">{configKey}</span>
      </h2>
      {phase === 'submitting' && (
        <p className="text-sm text-ink-mid">Publishing…</p>
      )}
      {phase === 'done' && (
        <p className="text-sm text-[--color-status-ok] text-center py-4">Published.</p>
      )}
      {phase === 'error' && (
        <div className="space-y-3">
          <ErrorChip>{errorMsg}</ErrorChip>
          <div className="flex gap-3">
            <Button type="button" onClick={() => { setPhase('stepup'); }} width="full">
              Retry
            </Button>
            <button type="button" onClick={onCancel} className="text-sm text-ink-low hover:text-ink-mid underline">
              Cancel
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ── RestoreModal ──────────────────────────────────────────────────────────────

interface RestoreModalProps {
  configKey: string;
  revisionId: number;
  restoreValue: string;
  onDone: () => void;
  onCancel: () => void;
}

type RestorePhase = 'stepup' | 'submitting' | 'done' | 'error';

function RestoreModal({ configKey, revisionId, restoreValue, onDone, onCancel }: RestoreModalProps) {
  const [phase, setPhase] = useState<RestorePhase>('stepup');
  const [errorMsg, setErrorMsg] = useState('');

  function handleVerified(token: string) {
    setPhase('submitting');
    void (async () => {
      const res = await apiFetch<{ ok: boolean; error?: string }>(
        `/admin/config/${encodeURIComponent(configKey)}/restore/${revisionId}`,
        { method: 'POST', headers: { 'X-Stepup-Token': token } },
      );
      if (!res.ok) {
        const err = (res.data as Record<string, unknown>)['error'] as string | undefined;
        setErrorMsg(err ?? 'Restore failed. Please try again.');
        setPhase('error');
        return;
      }
      setPhase('done');
      setTimeout(() => { onDone(); }, 600);
    })();
  }

  if (phase === 'stepup') {
    return (
      <StepUpModal
        actionIntent="config-restore"
        targetUserId={null}
        onVerified={handleVerified}
        onCancel={onCancel}
      />
    );
  }

  return (
    <ModalShell>
      <h2 className="text-base font-semibold text-ink-high font-mono">
        Restore <span className="text-primary">{configKey}</span>
      </h2>
      <p className="text-xs text-ink-mid font-mono">
        Value: <span className="text-ink-high">{restoreValue}</span>
      </p>
      {phase === 'submitting' && <p className="text-sm text-ink-mid">Restoring…</p>}
      {phase === 'done' && <p className="text-sm text-[--color-status-ok] text-center py-4">Restored.</p>}
      {phase === 'error' && (
        <div className="space-y-3">
          <ErrorChip>{errorMsg}</ErrorChip>
          <div className="flex gap-3">
            <Button type="button" onClick={() => { setPhase('stepup'); }} width="full">Retry</Button>
            <button type="button" onClick={onCancel} className="text-sm text-ink-low hover:text-ink-mid underline">Cancel</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ── KeyEditPanel ──────────────────────────────────────────────────────────────
// Shown when a key is selected.

interface KeyEditPanelProps {
  configKey: string;
  onClose: () => void;
  onPublished: () => void;
}

function KeyEditPanel({ configKey, onClose, onPublished }: KeyEditPanelProps) {
  const queryClient = useQueryClient();
  const [draftValue, setDraftValue] = useState('');
  const [notes, setNotes] = useState('');
  const [stageError, setStageError] = useState('');
  const [publishModal, setPublishModal] = useState(false);
  const [restoreModal, setRestoreModal] = useState<{ revisionId: number; restoreValue: string } | null>(null);

  const { data: detail, isLoading: detailLoading } = useQuery<KeyDetailResponse>({
    queryKey: ['config-key', configKey],
    queryFn: async () => {
      const res = await apiFetch<KeyDetailResponse>(`/admin/config/${encodeURIComponent(configKey)}`);
      if (!res.ok) throw new Error('Failed to load config key');
      return res.data;
    },
  });

  // Sync form fields when detail loads (replaces deprecated onSuccess).
  useEffect(() => {
    if (!detail) return;
    if (detail.draft) {
      setDraftValue(detail.draft.draft_value);
      setNotes(detail.draft.notes ?? '');
    } else {
      setDraftValue(detail.live_value ?? '');
      setNotes('');
    }
  }, [detail]);

  const { data: diff } = useQuery<DiffResponse>({
    queryKey: ['config-diff', configKey],
    queryFn: async () => {
      const res = await apiFetch<DiffResponse>(`/admin/config/${encodeURIComponent(configKey)}/diff`);
      if (!res.ok) throw new Error('Failed to load diff');
      return res.data;
    },
    enabled: !!(detail?.draft),
  });

  const { data: revisions } = useQuery<RevisionsResponse>({
    queryKey: ['config-revisions', configKey],
    queryFn: async () => {
      const res = await apiFetch<RevisionsResponse>(`/admin/config/${encodeURIComponent(configKey)}/revisions`);
      if (!res.ok) throw new Error('Failed to load revisions');
      return res.data;
    },
  });

  const stageMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch<{ ok: boolean; error?: string; validation_error?: string }>(
        `/admin/config/${encodeURIComponent(configKey)}/draft`,
        {
          method: 'PUT',
          body: JSON.stringify({ value: draftValue, notes: notes || undefined }),
        },
      );
      if (!res.ok) {
        const d = res.data as Record<string, unknown>;
        throw new Error(
          (d['validation_error'] as string | undefined) ??
          (d['error'] as string | undefined) ??
          'Stage failed',
        );
      }
      return res.data;
    },
    onSuccess: () => {
      setStageError('');
      void queryClient.invalidateQueries({ queryKey: ['config-key', configKey] });
      void queryClient.invalidateQueries({ queryKey: ['config-diff', configKey] });
      void queryClient.invalidateQueries({ queryKey: ['config-domains'] });
    },
    onError: (err: Error) => {
      setStageError(err.message);
    },
  });

  const discardMutation = useMutation({
    mutationFn: async () => {
      await apiFetch(`/admin/config/${encodeURIComponent(configKey)}/draft`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config-key', configKey] });
      void queryClient.invalidateQueries({ queryKey: ['config-diff', configKey] });
      void queryClient.invalidateQueries({ queryKey: ['config-domains'] });
    },
  });

  function handlePublishDone() {
    setPublishModal(false);
    void queryClient.invalidateQueries({ queryKey: ['config-key', configKey] });
    void queryClient.invalidateQueries({ queryKey: ['config-diff', configKey] });
    void queryClient.invalidateQueries({ queryKey: ['config-revisions', configKey] });
    void queryClient.invalidateQueries({ queryKey: ['config-domains'] });
    onPublished();
  }

  function handleRestoreDone() {
    setRestoreModal(null);
    void queryClient.invalidateQueries({ queryKey: ['config-key', configKey] });
    void queryClient.invalidateQueries({ queryKey: ['config-diff', configKey] });
    void queryClient.invalidateQueries({ queryKey: ['config-revisions', configKey] });
    void queryClient.invalidateQueries({ queryKey: ['config-domains'] });
    onPublished();
  }

  if (detailLoading || !detail) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
      </div>
    );
  }

  const hasDraft = !!detail.draft;
  const diffOk = diff?.validator_result?.ok ?? null;

  return (
    <div className="space-y-6" data-testid="key-edit-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-ink-low uppercase tracking-wider font-mono">Config key</p>
          <p className="font-mono text-sm text-primary break-all" data-testid="config-key-label">
            {configKey}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-ink-low hover:text-ink-high text-xs underline mt-1"
          data-testid="close-panel-btn"
        >
          Close
        </button>
      </div>

      {/* Live value */}
      <div className="rounded-sm border border-divider bg-panel p-3 space-y-1">
        <p className="text-xs text-ink-low uppercase tracking-wider">Live value</p>
        <p className="font-mono text-sm text-ink-high" data-testid="live-value">
          {detail.live_value !== null
            ? detail.live_value
            : <span className="text-ink-low italic">(no live row — game uses code default)</span>}
        </p>
      </div>

      {/* Edit form — only for editable keys */}
      {detail.editable ? (
        <div className="space-y-3">
          <Field label={`New value (${detail.validator_kind})`}>
            <Input
              id="config-draft-value"
              type="text"
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              className="font-mono"
              data-testid="draft-value-input"
            />
          </Field>

          <Field label="Notes (optional)">
            <Input
              id="config-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for change"
              data-testid="notes-input"
            />
          </Field>

          {stageError && (
            <div data-testid="stage-error">
              <ErrorChip>{stageError}</ErrorChip>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={() => { stageMutation.mutate(); }}
              disabled={stageMutation.isPending || !draftValue}
              data-testid="stage-btn"
            >
              {stageMutation.isPending ? 'Staging…' : 'Stage draft'}
            </Button>
            {hasDraft && (
              <button
                type="button"
                onClick={() => { discardMutation.mutate(); }}
                disabled={discardMutation.isPending}
                className="text-sm text-ink-low hover:text-ink-mid underline"
                data-testid="discard-btn"
              >
                Discard draft
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-sm border border-divider bg-muted p-3 text-xs text-ink-low font-mono">
          Read-only key — not in validator registry.
        </div>
      )}

      {/* Diff view */}
      {hasDraft && diff && (
        <div className="rounded-sm border border-border bg-panel p-4 space-y-3" data-testid="diff-panel">
          <p className="text-xs text-ink-low uppercase tracking-wider">Diff preview</p>
          <div className="grid grid-cols-2 gap-4 text-xs font-mono">
            <div>
              <p className="text-ink-low mb-1">Live</p>
              <p className="text-ink-mid bg-muted rounded-sm p-2 break-all" data-testid="diff-live">
                {diff.live_value ?? '(none)'}
              </p>
            </div>
            <div>
              <p className="text-ink-low mb-1">Draft</p>
              <p className="text-primary bg-muted rounded-sm p-2 break-all" data-testid="diff-draft">
                {diff.draft_value ?? '(none)'}
              </p>
            </div>
          </div>
          {diff.validator_result !== null && (
            <p
              className={`text-xs font-mono ${diffOk ? 'text-[--color-status-ok]' : 'text-[--color-status-error]'}`}
              data-testid="validator-result"
            >
              {diffOk ? 'Valid' : `Invalid: ${diff.validator_result.error ?? ''}`}
            </p>
          )}
          <Button
            type="button"
            onClick={() => { setPublishModal(true); }}
            disabled={diffOk === false}
            data-testid="publish-btn"
          >
            Publish
          </Button>
        </div>
      )}

      {/* Revision history */}
      {revisions && revisions.revisions.length > 0 && (
        <div className="space-y-2" data-testid="revisions-panel">
          <p className="text-xs text-ink-low uppercase tracking-wider">Revision history</p>
          <div className="rounded-sm border border-border overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left text-ink-mid">ID</th>
                  <th className="px-3 py-2 text-left text-ink-mid">Prior</th>
                  <th className="px-3 py-2 text-left text-ink-mid">New</th>
                  <th className="px-3 py-2 text-left text-ink-mid">By</th>
                  <th className="px-3 py-2 text-left text-ink-mid">At</th>
                  <th className="px-3 py-2 text-left text-ink-mid">Notes</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {revisions.revisions.map((rev) => (
                  <tr key={rev.revision_id} className="hover:bg-panel-elev" data-testid={`rev-row-${rev.revision_id}`}>
                    <td className="px-3 py-2 tabular-nums text-ink-low">{rev.revision_id}</td>
                    <td className="px-3 py-2 text-ink-mid">{rev.prior_value ?? '(none)'}</td>
                    <td className="px-3 py-2 text-ink-high">{rev.new_value}</td>
                    <td className="px-3 py-2 tabular-nums text-ink-low">{rev.published_by_staff_id}</td>
                    <td className="px-3 py-2 tabular-nums text-ink-low whitespace-nowrap">
                      {new Date(rev.published_at * 1000).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-ink-low max-w-xs truncate">{rev.notes ?? ''}</td>
                    <td className="px-3 py-2 text-right">
                      {detail.editable && (
                        <button
                          type="button"
                          onClick={() => { setRestoreModal({ revisionId: rev.revision_id, restoreValue: rev.new_value }); }}
                          className="text-primary hover:underline"
                          data-testid={`restore-btn-${rev.revision_id}`}
                        >
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Publish step-up modal */}
      {publishModal && (
        <PublishModal
          configKey={configKey}
          onDone={handlePublishDone}
          onCancel={() => { setPublishModal(false); }}
        />
      )}

      {/* Restore step-up modal */}
      {restoreModal && (
        <RestoreModal
          configKey={configKey}
          revisionId={restoreModal.revisionId}
          restoreValue={restoreModal.restoreValue}
          onDone={handleRestoreDone}
          onCancel={() => { setRestoreModal(null); }}
        />
      )}
    </div>
  );
}

// ── ConfigPage ────────────────────────────────────────────────────────────────

export function ConfigPage() {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [openDomains, setOpenDomains] = useState<Set<string>>(new Set(['banking']));

  const { data, isLoading, isError } = useQuery<DomainsResponse>({
    queryKey: ['config-domains'],
    queryFn: async () => {
      const res = await apiFetch<DomainsResponse>('/admin/config/domains');
      if (!res.ok) throw new Error('Failed to load config domains');
      return res.data;
    },
  });

  function toggleDomain(domain: string) {
    setOpenDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }

  const domains = data?.domains ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Config Editor"
        description="Stage and publish awake_config values. Publishing or restoring a value requires step-up re-auth."
      />

      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {isError && <ErrorChip>Failed to load config domains.</ErrorChip>}

      {!isLoading && !isError && (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          {/* Domain + key list */}
          <div className="space-y-2" data-testid="domains-panel">
            {domains.map((group) => (
              <div key={group.domain} className="rounded-sm border border-border overflow-hidden">
                {/* Domain header */}
                <button
                  type="button"
                  onClick={() => { toggleDomain(group.domain); }}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-mono font-semibold uppercase tracking-wider text-ink-mid hover:bg-panel-elev transition-colors bg-muted"
                  data-testid={`domain-${group.domain}`}
                >
                  <span>{group.domain}</span>
                  <span className="text-ink-low">{openDomains.has(group.domain) ? '▲' : '▼'}</span>
                </button>

                {/* Key list */}
                {openDomains.has(group.domain) && (
                  <ul className="divide-y divide-divider">
                    {group.keys.map((entry) => (
                      <li key={entry.key}>
                        <button
                          type="button"
                          onClick={() => { setSelectedKey(entry.key); }}
                          className={`w-full text-left px-3 py-2 text-xs font-mono hover:bg-panel-elev transition-colors flex items-center justify-between gap-2 ${
                            selectedKey === entry.key ? 'bg-primary-soft text-primary' : 'text-ink-mid'
                          }`}
                          data-testid={`key-btn-${entry.key}`}
                        >
                          <span className="truncate">{entry.key}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            {entry.live_value !== null && (
                              <span className="text-ink-low truncate max-w-[80px]">{entry.live_value}</span>
                            )}
                            {entry.has_draft && (
                              <span data-testid={`draft-pill-${entry.key}`}>
                                <StatusPill status="pending" className="shrink-0" />
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Edit panel */}
          <div>
            {selectedKey ? (
              <KeyEditPanel
                configKey={selectedKey}
                onClose={() => { setSelectedKey(null); }}
                onPublished={() => { /* panel stays open, data refreshed by mutation */ }}
              />
            ) : (
              <div className="flex items-center justify-center h-40 text-sm text-ink-low" data-testid="empty-panel">
                Select a config key to edit.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
